import { supabase } from '../lib/supabase';
import { getCurrentOrgId } from '../lib/getCurrentOrgId';

const BUCKET = 'invoice-inbox';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  const result = {};
  for (const key of Object.keys(obj)) {
    const camel = key?.replace(/_([a-z])/g, (_, c) => c?.toUpperCase());
    result[camel] = obj?.[key];
  }
  return result;
};

const mapInboxItem = (row) => {
  const item = toCamelCase(row);
  return item;
};

/**
 * Check if a file already exists in the inbox by name + size
 * @param {string} fileName
 * @param {number} fileSize
 * @returns {Promise<Object|null>} existing row or null
 */
export const checkDuplicate = async (fileName, fileSize) => {
  const { data, error } = await supabase
    ?.from('invoice_inbox')
    ?.select('id, file_name, file_size, status')
    ?.eq('file_name', fileName)
    ?.eq('file_size', fileSize)
    ?.limit(1);

  if (error) throw new Error(error.message);
  return data?.length > 0 ? mapInboxItem(data?.[0]) : null;
};

/**
 * Trigger AI extraction via the extract-invoice Edge Function.
 * Fire-and-forget safe — does not throw.
 * @param {string} invoiceId - the invoice_inbox row id
 * @returns {Promise<Object|null>}
 */
export const triggerExtraction = async (invoiceId) => {
  try {
    const { data, error } = await supabase?.functions?.invoke('extract-invoice', {
      body: { invoice_id: invoiceId },
    });
    if (error) console.error('Extraction failed', error);
    return data;
  } catch (err) {
    console.error('triggerExtraction error:', err);
    return null;
  }
};

/**
 * Upload a file to storage and insert a row in invoice_inbox,
 * then trigger AI extraction to populate extracted_data.
 * @param {File} file
 * @returns {Promise<Object>} the created inbox row
 */
export const uploadFile = async (file) => {
  const timestamp = Date.now();
  const safeName = file?.name?.replace(/[^a-zA-Z0-9._-]/g, '_');
  const filePath = `${timestamp}-${safeName}`;

  // Upload to storage with correct content type
  const { error: uploadError } = await supabase?.storage?.from(BUCKET)?.upload(filePath, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file?.type || 'application/pdf',
  });

  if (uploadError) throw new Error(uploadError.message);

  // Get current user id and organization_id
  const { data: { user } } = await supabase?.auth?.getUser();
  const orgId = await getCurrentOrgId();

  // Insert row in invoice_inbox (store file_path for fresh signed URL generation)
  const { data, error: insertError } = await supabase?.from('invoice_inbox')?.insert({
      file_path: filePath,
      file_name: file?.name,
      file_size: file?.size,
      status: 'new',
      ai_processed: false,
      extracted_data: {},
      created_by: user?.id || null,
      source: 'upload',
      received_at: new Date()?.toISOString(),
      organization_id: orgId,
    })?.select()?.single();

  if (insertError) throw new Error(insertError.message);

  const inboxRow = mapInboxItem(data);

  // Trigger AI extraction via Edge Function (fire-and-forget — don't block upload)
  // The Edge Function is the SOLE writer to ai_processed now
  triggerExtraction(inboxRow?.id)?.catch((err) => console.error('Background AI extraction error:', err));

  return inboxRow;
};

/**
 * Supprime une entree de la boite de reception : la ligne, puis son fichier.
 *
 * ORDRE VOLONTAIRE — la ligne AVANT le fichier. Un fichier orphelin ne coute
 * que de l'espace disque ; une ligne dont le PDF a ete detruit coute le
 * document lui-meme, et l'entree revient dans la liste sans rien a montrer.
 * C'est exactement ce qui s'est produit en production.
 *
 * `.select()` n'est pas decoratif : un DELETE que la RLS ecarte ne renvoie
 * AUCUNE erreur, seulement zero ligne. Sans reclamer les lignes supprimees,
 * l'echec passait pour un succes et l'appelant retirait l'entree de l'ecran.
 *
 * @param {string} id - id de la ligne invoice_inbox
 * @param {string|null} filePath - chemin de stockage (colonne file_path), peut etre null
 * @throws {Error} code 'INBOX_DELETE_NOT_PERMITTED' si zero ligne supprimee
 * @returns {Promise<void>}
 */
export const deleteInboxItem = async (id, filePath) => {
  const { data: deletedRows, error: dbError } = await supabase
    ?.from('invoice_inbox')
    ?.delete()
    ?.eq('id', id)
    ?.select('id');

  if (dbError) throw new Error(dbError.message);

  if (!deletedRows || deletedRows?.length === 0) {
    const err = new Error('INBOX_DELETE_NOT_PERMITTED');
    err.code = 'INBOX_DELETE_NOT_PERMITTED';
    throw err;
  }

  // La ligne est partie : le fichier peut suivre. Un retrait rate laisse un
  // orphelin dans le bucket, sans consequence pour l'utilisateur.
  if (filePath) {
    const { error: storageError } = await supabase?.storage
      ?.from('invoice-inbox')
      ?.remove([filePath]);
    if (storageError) {
      console.warn('[deleteInboxItem] storage remove failed:', storageError);
    }
  }
};

/**
 * Upload multiple files with duplicate detection.
 * Returns per-file results: { accepted: [], rejected: [] }
 * Calls onProgress({ current, total, accepted, rejected }) after each file.
 *
 * @param {FileList|File[]} files
 * @param {Function} onProgress
 * @returns {Promise<{ accepted: Array, rejected: Array }>}
 */
export const uploadFiles = async (files, onProgress) => {
  const fileArray = Array.from(files);
  const total = fileArray?.length;
  const accepted = [];
  const rejected = [];

  for (let i = 0; i < fileArray?.length; i++) {
    const file = fileArray?.[i];
    try {
      // Duplicate check: same name + same size
      const existing = await checkDuplicate(file?.name, file?.size);
      if (existing) {
        // Un CODE, pas une phrase : un service n'a pas acces a t(), et la logique
      // de traduction n'a rien a faire dans la couche donnees. La page traduit.
      rejected?.push({ name: file?.name, reason: 'duplicate' });
      } else {
        const row = await uploadFile(file);
        accepted?.push(row);
      }
    } catch (err) {
      rejected?.push({ name: file?.name, reason: err?.message || 'Upload failed' });
    }

    onProgress?.({
      current: i + 1,
      total,
      accepted: accepted?.length,
      rejected: rejected?.length,
    });
  }

  return { accepted, rejected };
};

/**
 * List inbox items with optional status filter, search, and uploader filter.
 * Joins with user_profiles to include uploader name.
 * @param {{ status?: string, search?: string, uploaderId?: string }} options
 * @returns {Promise<Array>}
 */
export const listInbox = async ({ status, search, uploaderId } = {}) => {
  let query = supabase
    ?.from('invoice_inbox')
    ?.select('*')
    ?.order('created_at', { ascending: false });

  if (status && status !== 'all') {
    query = query?.eq('status', status);
  }

  if (search) {
    query = query?.ilike('file_name', `%${search}%`);
  }

  if (uploaderId && uploaderId !== 'all') {
    query = query?.eq('created_by', uploaderId);
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);

  const items = (data || [])?.map(mapInboxItem);

  // Collect unique created_by user ids to fetch profiles
  const userIds = [...new Set(items?.map(i => i?.createdBy)?.filter(Boolean))];
  let profileMap = {};
  if (userIds?.length > 0) {
    const { data: profiles } = await supabase
      ?.from('user_profiles')
      ?.select('id, full_name, email')
      ?.in('id', userIds);
    (profiles || [])?.forEach(p => { profileMap[p?.id] = p; });
  }

  return items?.map(item => ({
    ...item,
    uploader: item?.createdBy ? (profileMap?.[item?.createdBy] || null) : null,
  }));
};

/**
 * Update the status of an inbox item
 * @param {string} id
 * @param {'new'|'ready'|'created'} status
 * @returns {Promise<Object>}
 */
export const setStatus = async (id, status) => {
  console.log("STATUS BEING SENT:", status);
  const { data, error } = await supabase?.from('invoice_inbox')?.update({ status })?.eq('id', id)?.select()?.single();

  if (error) throw new Error(error.message);
  return mapInboxItem(data);
};

/**
 * Link an inbox item to a sub_invoice
 * @param {string} id - inbox item id
 * @param {string} subInvoiceId - sub_invoice id
 * @returns {Promise<Object>}
 */
export const linkToInvoice = async (id, subInvoiceId) => {
  const { data, error } = await supabase?.from('invoice_inbox')?.update({ linked_invoice_id: subInvoiceId, status: 'created' })?.eq('id', id)?.select()?.single();

  if (error) throw new Error(error.message);
  return mapInboxItem(data);
};

/**
 * Link an inbox item to a project expense
 * @param {string} id - inbox item id
 * @param {string} expenseId - project_expenses id
 * @returns {Promise<Object>}
 */
export const linkToExpense = async (id, expenseId) => {
  const { data, error } = await supabase?.from('invoice_inbox')?.update({ linked_expense_id: expenseId, status: 'created' })?.eq('id', id)?.select()?.single();

  if (error) throw new Error(error.message);
  return mapInboxItem(data);
};

/**
 * Generate a fresh signed URL for a stored file
 * @param {string} filePath - the storage path of the file
 * @param {number} expiresIn - seconds until expiry (default 1 hour)
 * @returns {Promise<string>} signed URL
 */
export const getSignedUrl = async (filePath, expiresIn = 3600) => {
  const { data, error } = await supabase?.storage?.from(BUCKET)?.createSignedUrl(filePath, expiresIn);
  if (error) throw new Error(error.message);
  return data?.signedUrl;
};

/**
 * Update extracted_data on an inbox item to persist AI + matching results
 * @param {string} id - inbox item id
 * @param {Object} extractedData - matching results to merge into extracted_data
 * @returns {Promise<Object>}
 */
export const updateExtractedData = async (id, extractedData) => {
  // First fetch current extracted_data to merge
  const { data: current, error: fetchError } = await supabase
    ?.from('invoice_inbox')
    ?.select('extracted_data')
    ?.eq('id', id)
    ?.single();

  if (fetchError) throw new Error(fetchError.message);

  const merged = { ...(current?.extracted_data || {}), ...extractedData };

  const { data, error } = await supabase
    ?.from('invoice_inbox')
    ?.update({ extracted_data: merged })
    ?.eq('id', id)
    ?.select()
    ?.single();

  if (error) throw new Error(error.message);
  return mapInboxItem(data);
};

/**
 * Set the document_type on an inbox item (persists user's choice)
 * @param {string} id - inbox item id
 * @param {'invoice'|'expense'} documentType
 * @returns {Promise<Object>}
 */
export const setDocumentType = async (id, documentType) => {
  const { data, error } = await supabase
    ?.from('invoice_inbox')
    ?.update({ document_type: documentType })
    ?.eq('id', id)
    ?.select()
    ?.single();

  if (error) throw new Error(error.message);
  return mapInboxItem(data);
};

/**
 * Set the ai_suggested_type on an inbox item
 * @param {string} id - inbox item id
 * @param {'invoice'|'expense'} suggestedType
 * @returns {Promise<Object>}
 */
export const setAiSuggestedType = async (id, suggestedType) => {
  const { data, error } = await supabase
    ?.from('invoice_inbox')
    ?.update({ ai_suggested_type: suggestedType })
    ?.eq('id', id)
    ?.select()
    ?.single();

  if (error) throw new Error(error.message);
  return mapInboxItem(data);
};

/**
 * Set the ai_processed flag on an inbox item
 * @param {string} id - inbox item id
 * @param {boolean} value
 * @returns {Promise<Object>}
 */
export const setAiProcessed = async (id, value) => {
  const { data, error } = await supabase
    ?.from('invoice_inbox')
    ?.update({ ai_processed: value })
    ?.eq('id', id)
    ?.select()
    ?.single();

  if (error) throw new Error(error.message);
  return mapInboxItem(data);
};

/**
 * Fetch a single inbox item by id
 * @param {string} id - inbox item id
 * @returns {Promise<Object>}
 */
export const getById = async (id) => {
  const { data, error } = await supabase
    ?.from('invoice_inbox')
    ?.select('*')
    ?.eq('id', id)
    ?.single();

  if (error) throw new Error(error.message);
  return mapInboxItem(data);
};

/**
 * Look up the best cost code for a supplier+project from the corrections training table.
 * Returns the project_cost_id with the highest confirmation_count, or null if none found.
 *
 * Priority order:
 *   1. Exact supplier name match in same project (highest confirmation_count wins)
 *   2. Exact supplier name match across any project (cross-project learning)
 *
 * @param {string} supplierName - raw supplier name from invoice
 * @param {string} projectId - current project id
 * @returns {Promise<{ projectCostId: string, confirmationCount: number, wasOverride: boolean } | null>}
 */
export const getBestCostCodeFromHistory = async (supplierName, projectId) => {
  if (!supplierName) return null;
  const normalized = supplierName?.trim()?.toLowerCase();

  try {
    // 1. Try project-specific match first
    const { data: projectMatches } = await supabase
      ?.from('cost_code_corrections')
      ?.select('project_cost_id, confirmation_count, was_override')
      ?.eq('supplier_name', normalized)
      ?.eq('project_id', projectId)
      ?.order('confirmation_count', { ascending: false })
      ?.limit(1);

    if (projectMatches?.length > 0) {
      return {
        projectCostId: projectMatches?.[0]?.project_cost_id,
        confirmationCount: projectMatches?.[0]?.confirmation_count,
        wasOverride: projectMatches?.[0]?.was_override,
      };
    }

    // 2. Fall back to cross-project match
    const { data: globalMatches } = await supabase
      ?.from('cost_code_corrections')
      ?.select('project_cost_id, confirmation_count, was_override')
      ?.eq('supplier_name', normalized)
      ?.order('confirmation_count', { ascending: false })
      ?.limit(1);

    if (globalMatches?.length > 0) {
      return {
        projectCostId: globalMatches?.[0]?.project_cost_id,
        confirmationCount: globalMatches?.[0]?.confirmation_count,
        wasOverride: globalMatches?.[0]?.was_override,
      };
    }

    return null;
  } catch (err) {
    console.error('getBestCostCodeFromHistory error:', err);
    return null;
  }
};

/**
 * Record a cost code confirmation or override as a training signal.
 * If the same supplier+project+cost_code pair already exists, increments confirmation_count.
 * If it's a new pair, inserts a new record.
 *
 * @param {Object} params
 * @param {string} params.supplierName - raw supplier name from invoice
 * @param {string} params.projectId - project id
 * @param {string|null} params.subcontractId - subcontract id (optional)
 * @param {string} params.projectCostId - the cost code that was ultimately used
 * @param {boolean} params.wasOverride - true if user changed AI suggestion, false if confirmed
 * @param {string|null} params.originalAiSuggestion - cost code AI originally suggested
 * @returns {Promise<void>}
 */
export const recordCostCodeCorrection = async ({
  supplierName,
  projectId,
  subcontractId = null,
  projectCostId,
  wasOverride = false,
  originalAiSuggestion = null,
}) => {
  if (!supplierName || !projectId || !projectCostId) return;
  const normalized = supplierName?.trim()?.toLowerCase();

  try {
    const { data: { user } } = await supabase?.auth?.getUser();

    // Upsert: if same supplier+project+cost_code exists, increment count
    const { error } = await supabase
      ?.from('cost_code_corrections')
      ?.upsert(
        {
          supplier_name: normalized,
          project_id: projectId,
          subcontract_id: subcontractId || null,
          project_cost_id: projectCostId,
          was_override: wasOverride,
          original_ai_suggestion: originalAiSuggestion || null,
          confirmation_count: 1,
          created_by: user?.id || null,
        },
        {
          onConflict: 'supplier_name,project_id,project_cost_id',
          ignoreDuplicates: false,
        }
      );

    if (error) {
      // If upsert fails (e.g. conflict handling), try manual increment
      await supabase?.rpc('increment_cost_code_correction', {
        p_supplier_name: normalized,
        p_project_id: projectId,
        p_project_cost_id: projectCostId,
        p_was_override: wasOverride,
      });
    }
  } catch (err) {
    console.error('recordCostCodeCorrection error:', err);
    // Non-critical — don't throw, just log
  }
};

const invoiceInboxService = {
  uploadFile,
  uploadFiles,
  checkDuplicate,
  listInbox,
  setStatus,
  linkToInvoice,
  linkToExpense,
  getSignedUrl,
  updateExtractedData,
  getById,
  setDocumentType,
  setAiSuggestedType,
  setAiProcessed,
  getBestCostCodeFromHistory,
  recordCostCodeCorrection,
};
export default invoiceInboxService;
